import { create } from 'zustand';
import { ABTestWizardData, ABTestWizardStep } from './types';

interface ABTestWizardStore {
  currentStep: number;
  steps: ABTestWizardStep[];
  data: ABTestWizardData;
  setCurrentStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  setTestInfo: (name: string, description?: string) => void;
  toggleVideo: (video: ABTestWizardData['selectedVideos'][0]) => void;
  setTrafficMode: (mode: 'auto' | 'manual') => void;
  updatePercentage: (videoId: string, percentage: number) => void;
  reorderVideos: (videos: ABTestWizardData['selectedVideos']) => void;
  validateCurrentStep: () => boolean;
  completeStep: (stepIndex: number) => void;
  reset: () => void;
}

const initialSteps: ABTestWizardStep[] = [
  {
    id: 'select-videos',
    title: 'Selecionar vídeos',
    description: 'Escolha os vídeos para o teste A/B',
    isCompleted: false
  },
  {
    id: 'traffic-distribution',
    title: 'Distribuição de Tráfego',
    description: 'Configure como o tráfego será distribuído',
    isCompleted: false
  }
];

const initialData: ABTestWizardData = {
  testName: '',
  testDescription: '',
  selectedVideos: [],
  trafficDistribution: {
    mode: 'auto',
    percentages: {}
  }
};

export const useABTestWizardStore = create<ABTestWizardStore>((set, get) => ({
  currentStep: 0,
  steps: initialSteps,
  data: initialData,

  setCurrentStep: (step) => set({ currentStep: step }),

  nextStep: () => {
    const { currentStep, steps } = get();
    if (currentStep < steps.length - 1) {
      set({ currentStep: currentStep + 1 });
    }
  },

  prevStep: () => {
    const { currentStep } = get();
    if (currentStep > 0) {
      set({ currentStep: currentStep - 1 });
    }
  },

  setTestInfo: (name, description) => {
    set((state) => ({
      data: {
        ...state.data,
        testName: name,
        testDescription: description || ''
      }
    }));
  },

  toggleVideo: (video) => {
    set((state) => {
      const isSelected = state.data.selectedVideos.some(v => v.id === video.id);
      let newVideos: ABTestWizardData['selectedVideos'];
      
      if (isSelected) {
        newVideos = state.data.selectedVideos.filter(v => v.id !== video.id);
      } else {
        newVideos = [...state.data.selectedVideos, video];
      }

      // Recalcular porcentagens automaticamente
      const newPercentages: Record<string, number> = {};
      if (newVideos.length > 0) {
        const equalPercentage = Math.floor(100 / newVideos.length);
        newVideos.forEach((v, index) => {
          newPercentages[v.id] = index === newVideos.length - 1
            ? 100 - equalPercentage * (newVideos.length - 1)
            : equalPercentage;
        });
      }

      return {
        data: {
          ...state.data,
          selectedVideos: newVideos,
          trafficDistribution: {
            ...state.data.trafficDistribution,
            percentages: newPercentages
          }
        }
      };
    });
  },

  setTrafficMode: (mode) => {
    set((state) => {
      if (mode === 'auto') {
        const videos = state.data.selectedVideos;
        const newPercentages: Record<string, number> = {};
        if (videos.length > 0) {
          const equalPercentage = Math.floor(100 / videos.length);
          videos.forEach((v, index) => {
            newPercentages[v.id] = index === videos.length - 1
              ? 100 - equalPercentage * (videos.length - 1)
              : equalPercentage;
          });
        }
        return {
          data: {
            ...state.data,
            trafficDistribution: {
              mode: 'auto',
              percentages: newPercentages
            }
          }
        };
      }
      return {
        data: {
          ...state.data,
          trafficDistribution: {
            ...state.data.trafficDistribution,
            mode: 'manual'
          }
        }
      };
    });
  },

  updatePercentage: (videoId, percentage) => {
    set((state) => ({
      data: {
        ...state.data,
        trafficDistribution: {
          ...state.data.trafficDistribution,
          percentages: {
            ...state.data.trafficDistribution.percentages,
            [videoId]: percentage
          }
        }
      }
    }));
  },

  reorderVideos: (videos) => {
    set((state) => {
      // Recalcular porcentagens com nova ordem
      const newPercentages: Record<string, number> = {};
      if (videos.length > 0) {
        const equalPercentage = Math.floor(100 / videos.length);
        videos.forEach((v, index) => {
          newPercentages[v.id] = index === videos.length - 1
            ? 100 - equalPercentage * (videos.length - 1)
            : equalPercentage;
        });
      }

      return {
        data: {
          ...state.data,
          selectedVideos: videos,
          trafficDistribution: {
            mode: 'auto',
            percentages: newPercentages
          }
        }
      };
    });
  },

  validateCurrentStep: () => {
    const { currentStep, data } = get();
    
    switch (currentStep) {
      case 0: // Select Videos
        return data.selectedVideos.length >= 2;
      case 1: // Traffic Distribution
        const total = Object.values(data.trafficDistribution.percentages).reduce((a, b) => a + b, 0);
        return total === 100;
      default:
        return false;
    }
  },

  completeStep: (stepIndex) => {
    set((state) => ({
      steps: state.steps.map((step, index) => 
        index === stepIndex ? { ...step, isCompleted: true } : step
      )
    }));
  },

  reset: () => {
    set({
      currentStep: 0,
      steps: initialSteps.map(step => ({ ...step, isCompleted: false })),
      data: initialData
    });
  }
}));
